import type { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AI_DURABLE_AGENT_ID, isAIChatContextGuardStatus } from '@jms/model';
import type { AIDurableApproveRequestBody, AIDurableCardAnswerResponse, AIDurableDeclineRequestBody, AIDurableRouteError, AIDurableSendIntent, AIDurableSendRequestBody, AIDurableSendResponse, AIDurableStopResponse, AIDurableThreadState, AIDurableThreadStatusResponse } from '@jms/model';
import type { AgentChatStatus } from './agent.chat.adapter';
import type { DurableThreadRoute, DurableSubscriptionEvent, DurableSubscriptionEnd, DurableSubscriptionHandle, DurableTransportDeps } from './durable.agent.transport.types';
import { DurableRouteError } from './errors';

/**
 * Transport client for the six durable thread routes:
 *   /api/ai/agents/workbuddy-agent/threads/:threadId/{send,subscribe,stop,status,approve,decline}
 *
 * JSON routes go through HttpClient (the interceptors add the pass and the nonce). The subscribe
 * route is a long-lived SSE read over `fetch` (EventSource cannot carry the Authorization header),
 * parsed here into `{ id, chunk }` events so the adapter can drop the whole-run replay jms-ai sends
 * on every reattach. Every route failure becomes a `DurableRouteError` carrying the calm message and
 * the upstream code the adapter branches on.
 *
 * Pure of Angular on purpose (types only): `durable.agent.transport.service.ts` is the injectable
 * that wires HttpClient, the pass and the nonce in.
 */

export const DURABLE_AGENT_BASE_URL = '/api/ai/agents';

export const DURABLE_UNREACHABLE_CODE = 'UPSTREAM_UNREACHABLE';
/**
 * A refusal by the API's AI usage-limit door check (429 carrying a `limit` object). It has no
 * upstream code of its own; the `limit` object is what tells it apart from the route throttler's 429.
 */
export const DURABLE_USAGE_LIMIT_CODE = 'AI_USAGE_LIMIT_REACHED';
const DURABLE_UNREACHABLE_MESSAGE = 'The agent could not be reached. Please try again in a moment.';

export function durableThreadRoute(threadId: string, route: DurableThreadRoute): string {

  return `${DURABLE_AGENT_BASE_URL}/${AI_DURABLE_AGENT_ID}/threads/${encodeURIComponent(threadId)}/${route}`;
}

function isRouteErrorBody(value: unknown): value is AIDurableRouteError {

  return typeof value === 'object' && value !== null && typeof (value as { message?: unknown }).message === 'string';
}

/** The shape of an HttpClient failure (`HttpErrorResponse`): an HTTP status plus the parsed body. */
function isHttpFailure(error: unknown): error is { status: number; error: unknown } {

  return typeof error === 'object' && error !== null && typeof (error as { status?: unknown }).status === 'number' && 'error' in error;
}

/** The usage door check's body: VNext's `{ message, limit }`, the same one the stream route refuses with. */
function isUsageLimitBody(value: unknown): value is { message: string; limit: unknown } {

  return isRouteErrorBody(value) && !!(value as { limit?: unknown }).limit;
}

/** Normalises any failure from a durable route into the `{ status, code, message }` the adapter reads. */
export function toDurableRouteError(error: unknown): DurableRouteError {

  if (error instanceof DurableRouteError) return error;
  if (isHttpFailure(error)) {
    if (error.status === 429 && isUsageLimitBody(error.error)) {
      return new DurableRouteError(error.status, DURABLE_USAGE_LIMIT_CODE, error.error.message);
    }
    if (isRouteErrorBody(error.error)) {
      const contextStatus = (error.error as { contextStatus?: unknown }).contextStatus;
      return new DurableRouteError(error.status, typeof error.error.code === 'string' ? error.error.code : DURABLE_UNREACHABLE_CODE, error.error.message, isAIChatContextGuardStatus(contextStatus) ? contextStatus : null);
    }
    return new DurableRouteError(error.status, DURABLE_UNREACHABLE_CODE, DURABLE_UNREACHABLE_MESSAGE);
  }
  return new DurableRouteError(0, DURABLE_UNREACHABLE_CODE, DURABLE_UNREACHABLE_MESSAGE);
}

/**
 * What the sending window believes: `steer` while it can see the agent working (its own status or
 * the last status read), `start` otherwise. The adapter reads status first when it has never read it,
 * so this never guesses on a thread it knows nothing about.
 */
export function resolveSendIntent(localStatus: AgentChatStatus, threadState: AIDurableThreadState | null): AIDurableSendIntent {

  if (localStatus === 'streaming' || localStatus === 'submitted') return 'steer';
  return threadState === 'running' ? 'steer' : 'start';
}

// ─── Subscription parsing ────────────────────────────────────────────────────

/** Parses one SSE block (`id:` and `data:` lines, comments ignored). */
export function parseDurableSseEvent(block: string): DurableSubscriptionEvent | null {

  let id: string | null = null;
  let data = '';
  for (const line of block.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('id:')) {
      id = line.slice(3).trim();
    } else if (line.startsWith('data:')) {
      data += (data ? '\n' : '') + line.slice(5).trim();
    }
  }
  if (!data || data === '[DONE]') return null;
  try {
    return { id, chunk: JSON.parse(data) };
  } catch {
    console.error('[AgentDurable] Failed to parse subscription event:', data);
    return null;
  }
}

/** Stateful splitter: feed decoded text, receive the complete events it contained. */
export function createDurableSseParser(): (text: string) => DurableSubscriptionEvent[] {

  let buffer = '';
  return (text: string) => {
    buffer += text.replace(/\r\n/g, '\n');
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';
    const events: DurableSubscriptionEvent[] = [];
    for (const block of blocks) {
      const event = parseDurableSseEvent(block);
      if (event) events.push(event);
    }
    return events;
  };
}

/**
 * Drops events already applied. jms-ai replays a run from its first chunk on every reattach and
 * gives the same chunk the same id, so the id alone identifies a replay. The fingerprint guards the
 * one case the contract does not promise: a reader that only received the remainder would restart
 * its counter, and an id collision with different content must still be applied.
 *
 * ONE instance belongs to the window, not to a subscription: `subscribe` takes it as a required
 * argument precisely so a reconnect cannot start a fresh one and re-apply a run this window has
 * already rendered. `forgetRun` is memory reclamation for a run whose chunks the caller now drops
 * wholesale on its own; it is never a licence to replay that run again.
 */
export class DurableReplayFilter {

  private readonly seen = new Map<string, string>();

  /** True when the event should be applied: it has no id, or its id has not been seen with this content. Records it. */
  public accept(event: DurableSubscriptionEvent): boolean {

    if (!event.id) return true;
    const fingerprint = DurableReplayFilter.fingerprint(event.chunk);
    if (this.seen.get(event.id) === fingerprint) return false;
    this.seen.set(event.id, fingerprint);
    return true;
  }

  /** Forget a finished run's ids so the map never grows across a long-lived thread. */
  public forgetRun(runId: string): void {

    for (const id of [...this.seen.keys()]) {
      if (id.startsWith(`${runId}:`)) this.seen.delete(id);
    }
  }

  /** Forgets every id: only for a full reset where the transcript is reloaded from storage, never on a reconnect. */
  public clear(): void {

    this.seen.clear();
  }

  private static fingerprint(chunk: unknown): string {

    const text = typeof chunk === 'string' ? chunk : JSON.stringify(chunk) ?? '';
    let hash = 5381;
    for (let index = 0; index < text.length; index += 1) {
      hash = ((hash << 5) + hash + text.charCodeAt(index)) | 0;
    }
    return `${text.length}:${hash}`;
  }
}

// ─── Subscription handle ─────────────────────────────────────────────────────

function isAbortError(error: unknown): boolean {

  return error instanceof Error && (error.name === 'AbortError' || error.message.toLowerCase().includes('aborted'));
}

async function readErrorResponse(response: Response): Promise<DurableRouteError> {

  try {
    const body: unknown = await response.json();
    if (isRouteErrorBody(body)) {
      return new DurableRouteError(response.status, typeof body.code === 'string' ? body.code : DURABLE_UNREACHABLE_CODE, body.message);
    }
  } catch {
    // Non-JSON error body: fall through to the generic message.
  }
  return new DurableRouteError(response.status, DURABLE_UNREACHABLE_CODE, DURABLE_UNREACHABLE_MESSAGE);
}

/**
 * The six durable thread routes as promises. Every failure rejects with a `DurableRouteError` carrying
 * the calm message and the upstream code; nothing is retried here, the adapter decides.
 */
export class DurableAgentTransport {

  /** `deps` supplies HttpClient, the pass, fetch and the nonce, so this class stays free of Angular injection. */
  constructor(private readonly deps: DurableTransportDeps) {}

  /** Sends a message: starts a run on an idle thread or steers the running one, per the answer's `mode`. */
  public async send(threadId: string, body: AIDurableSendRequestBody): Promise<AIDurableSendResponse> {

    return this.request(() => this.deps.http.post<AIDurableSendResponse>(durableThreadRoute(threadId, 'send'), body));
  }

  /** Stops all work on the thread; the answer says whether it was confirmed and hands back unconsumed steering. */
  public async stop(threadId: string): Promise<AIDurableStopResponse> {

    return this.request(() => this.deps.http.post<AIDurableStopResponse>(durableThreadRoute(threadId, 'stop'), {}));
  }

  /** Reads the thread's derived state (running, waiting, idle, failed, expired) and its queued steering. */
  public async status(threadId: string): Promise<AIDurableThreadStatusResponse> {

    return this.request(() => this.deps.http.get<AIDurableThreadStatusResponse>(durableThreadRoute(threadId, 'status')));
  }

  /** Approves the waiting card (or answers an interview) and resumes the run. */
  public async approve(threadId: string, body: AIDurableApproveRequestBody): Promise<AIDurableCardAnswerResponse> {

    return this.request(() => this.deps.http.post<AIDurableCardAnswerResponse>(durableThreadRoute(threadId, 'approve'), body));
  }

  /** Declines the waiting card and resumes the run with the refusal. */
  public async decline(threadId: string, body: AIDurableDeclineRequestBody): Promise<AIDurableCardAnswerResponse> {

    return this.request(() => this.deps.http.post<AIDurableCardAnswerResponse>(durableThreadRoute(threadId, 'decline'), body));
  }

  /**
   * Opens the subscription and feeds each accepted event to `onEvent`. Closing the handle aborts the
   * fetch, which jms-api forwards as an unsubscribe of this reader only. The filter is supplied by
   * the caller and must outlive this subscription, or every reconnect re-applies the whole replay.
   */
  public subscribe(threadId: string, onEvent: (event: DurableSubscriptionEvent) => void, filter: DurableReplayFilter): DurableSubscriptionHandle {

    const controller = new AbortController();
    let closedByCaller = false;

    const ended = (async (): Promise<DurableSubscriptionEnd> => {
      try {
        const token = await this.deps.getAuthToken();
        const nonce = this.deps.nonce();
        const response = await this.deps.fetchImpl(durableThreadRoute(threadId, 'subscribe'), {
          method: 'GET',
          headers: { Accept: 'text/event-stream', Authorization: token, ...(nonce ? { Nonce: nonce } : {}) },
          signal: controller.signal,
        });
        if (!response.ok) {
          throw await readErrorResponse(response);
        }
        const reader = response.body?.getReader();
        if (!reader) {
          throw new DurableRouteError(response.status, DURABLE_UNREACHABLE_CODE, DURABLE_UNREACHABLE_MESSAGE);
        }
        const decoder = new TextDecoder();
        const parse = createDurableSseParser();
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            for (const event of parse(decoder.decode(value, { stream: true }))) {
              if (filter.accept(event)) onEvent(event);
            }
          }
        } finally {
          reader.releaseLock();
        }
        return { reason: closedByCaller ? 'closed' : 'ended' };
      } catch (error) {
        if (closedByCaller || isAbortError(error)) return { reason: 'closed' };
        return { reason: 'error', error: toDurableRouteError(error) };
      }
    })();

    return {
      close: () => {
        closedByCaller = true;
        controller.abort();
      },
      ended,
    };
  }

  private async request<T>(call: () => ReturnType<DurableTransportDeps['http']['post']>): Promise<T> {

    try {
      return await firstValueFrom(call()) as T;
    } catch (error) {
      throw toDurableRouteError(error);
    }
  }
}
